import React, { useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { useDropzone } from 'react-dropzone';
import { Modal, FileItemLayout, UploadDocumentsModalLayout, LoadingIndicator } from '../ui';
import { CreateMessageAddAttachmentsUploadedFileItem } from '../Worklist/Shared/WorklistCreateMessage/CreateMessageAddAttachmentsModal/Sections/Modules/CreateMessageAddAttachmentsUploadedFileItem';
import { NOTIFICATION_MESSAGES } from '../../localization/Notifications/dictionary';
import renderNotification from '../../helpers/notifications/renderNotification';
import ConfirmLeaveModal from '../ConfirmLeaveModal/ConfirmLeaveModal';
import { isEmptyArray } from '../../helpers/utility';
import { selectFileContainers } from '../../redux/fileContainers/selectors';
import { selectUploadsTempContainer } from '../../redux/fileUpload/selectors';
import { uploadFile, clearUploadsTempContainer } from '../../redux/fileUpload/slice';

const { FileListWrapper } = FileItemLayout;

const {
  UploadDocumentsUpdateOverlay,
  UploadDocumentsUploadDropzoneWrapper,
  UploadDocumentsUploadDropzoneText,
  UploadDocumentsUploadDropzoneTextWrapper,
  UploadDocumentsHighlightedText,
  UploadDocumentsConstraints,
  ModalWrapper,
  UploadDocumentsModalFileListHeader,
  UploadDocumentsModalFileListHeaderText,
  UploadDocumentsModalFileListCounter,
  UploadDocumentsModalDescription
} = UploadDocumentsModalLayout;

/**
 * Renders a modal component for uploading documents with drag-and-drop functionality.
 *
 * @component
 * @param {object} props - Props for the component.
 * @param {boolean} props.isModalOpen - Indicates whether the modal is open.
 * @param {string} props.fileContainerId - The ID of the file container used for uploads.
 * @param {object[]} props.fileContainers - Array containing all possible file containers.
 * @param {function} props.handleToggleModal - Function to toggle the modal's open/close state.
 * @param {function} props.uploadFile - Function to handle the file upload.
 * @param {function} props.onUploadComplete - Function to call when upload is complete.
 * @param {object[]} props.uploadsTempContainer - Temporary storage for uploads.
 * @param {function} props.clearUploadsTempContainer - Function to clear the temporary storage.
 * @param {number} [props.maxNumberOfFiles=10] - Maximum number of files that can be uploaded.
 * @param {string} props.title - Title of the modal.
 * @param {string} props.description - Description of the modal.
 * @param {object} props.allowedFileTypes - Allowed file types for upload.
 */
const UploadDocumentsModalComponent = ({
  isModalOpen,
  fileContainerId = null,
  fileContainers,
  handleToggleModal,
  uploadFile,
  onUploadComplete,
  uploadsTempContainer,
  clearUploadsTempContainer,
  maxNumberOfFiles = 10,
  title,
  description,
  allowedFileTypes,
  maxFileSize
}) => {
  const [attachments, setAttachments] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showConfirmLeaveModal, setShowConfirmLeaveModal] = useState(false);
  const [showLoadingIndicator, setShowLoadingIndicator] = useState(false);
  const [uploadWasSuccessful, setUploadWasSuccessful] = useState(false);
  const [expectedUploads, setExpectedUploads] = useState(0);

  // check if all uploads are done
  useEffect(() => {
    if (showLoadingIndicator && expectedUploads > 0 && uploadsTempContainer.length === expectedUploads) {
      setIsLoading(false);
      setUploadWasSuccessful(true);
      setTimeout(() => {
        onUploadComplete();
        handleCloseModal();
        setShowLoadingIndicator(false);
      }, 1000);
    }
  }, [uploadsTempContainer.length, expectedUploads, showLoadingIndicator]);

  const acceptedMimeTypes = useMemo(() => Object.keys(allowedFileTypes?.accept || {}), [allowedFileTypes]);
  const acceptedExtensions = useMemo(
    () =>
      Object.values(allowedFileTypes?.accept || {})
        .flat()
        .map((ext) => ext.toLowerCase()),
    [allowedFileTypes]
  );

  const isAllowedFileType = (file) => {
    const mimeType = file?.type;
    if (mimeType && acceptedMimeTypes.includes(mimeType)) {
      return true;
    }

    const fileName = file?.name || '';
    const fileExtension = fileName.includes('.') ? `.${fileName.split('.').pop().toLowerCase()}` : '';
    return acceptedExtensions.includes(fileExtension);
  };

  const onDrop = (acceptedFiles) => {
    const newFiles = [];
    const totalAcceptedFiles = acceptedFiles.length;
    let filesIterator = 0;
    acceptedFiles.forEach((file) => {
      if (!isAllowedFileType(file)) {
        const rejectedFile = [
          {
            errors: [
              {
                code: 'file-invalid-type',
                message: 'Invalid file type'
              }
            ],
            file
          }
        ];
        onDropRejected(rejectedFile);
        return;
      }

      const _file = {
        blob: file,
        result: null,
        id: null
      };
      const reader = new FileReader();

      // eslint-disable-next-line no-console
      reader.onabort = () => console.warn('file reading was aborted');
      // eslint-disable-next-line no-console
      reader.onerror = () => console.warn('file reading has failed');

      reader.onload = () => {
        // Do whatever you want with the file contents
        _file.result = reader.result;

        // we set the timestamp as the id because we need a unique id for the file
        // we need to use the timestamp because the file name is not unique
        // we add the index to the timestamp because the timestamp is not unique if we upload multiple files at once
        _file.id = new Date().getTime() + filesIterator;

        // if it's the last file, we need to update the local state
        // we have to use this approach because the state is not updated instantly
        if (filesIterator === totalAcceptedFiles - 1) {
          newFiles.push(_file);
          setAttachments([...attachments, ...newFiles]);
        } else {
          filesIterator += 1;
          newFiles.push(_file);
        }
      };
      reader.readAsArrayBuffer(file);
    });
  };

  const onDropRejected = (rejectedFiles) => {
    if (rejectedFiles.length > numberOfFilesAllowed) {
      const wordingForAttachmentsBasedOnNumber = maxNumberOfFiles === 1 ? ' Datei' : ' Dateien';
      const _errorMessage = NOTIFICATION_MESSAGES.message_attachment_max_files_exceeded.replace(
        '$NUMBER_OF_FILES_ALLOWED',
        `${maxNumberOfFiles}${wordingForAttachmentsBasedOnNumber}`
      );
      renderNotification({ type: 'error', message: _errorMessage });
      return;
    }

    rejectedFiles.forEach((rejectedFile) => {
      const { errors, file } = rejectedFile;
      const error = errors[0];
      if (error.code === 'file-invalid-type') {
        const errorMessage = NOTIFICATION_MESSAGES.message_attachment_invalid_file_type.replace(
          '$FILE_NAME',
          file.name
        );
        renderNotification({ type: 'error', message: errorMessage });
      } else if (error.code === 'file-too-large') {
        const errorMessage = NOTIFICATION_MESSAGES.message_attachment_size_exceeded.replace('$FILE_NAME', file.name);
        renderNotification({ type: 'error', message: errorMessage });
      }
    });
  };

  const numberOfFilesAllowed = maxNumberOfFiles - attachments.length;
  const disableDropzone = numberOfFilesAllowed === 0;
  const { accept } = allowedFileTypes;
  const options = {
    onDrop,
    onDropRejected,
    multiple: maxNumberOfFiles > 1,
    maxSize: maxFileSize.byte,
    maxFiles: numberOfFilesAllowed,
    disabled: disableDropzone,
    accept
  };
  const { getRootProps, getInputProps } = useDropzone(options);

  const handleRemoveFile = (file) => {
    const _files = attachments?.filter((_file) => _file.id !== file.id);
    setAttachments(_files);
  };

  const renderFilesList = () => {
    return attachments?.map((file) => (
      <CreateMessageAddAttachmentsUploadedFileItem
        key={file.id}
        file={file}
        handleRemove={() => handleRemoveFile(file)}
        withBackground
      />
    ));
  };

  const handleOnSubmit = async () => {
    if (isLoading || isEmptyArray(attachments)) {
      return;
    }

    setShowLoadingIndicator(true);
    setIsLoading(true);
    setExpectedUploads(attachments.length);

    const uploadPromises = attachments.map(
      (attachment) =>
        new Promise((resolve, reject) => {
          uploadFile({
            file: attachment.blob,
            fileContainerId,
            callback: (data) => {
              if (data) {
                resolve(data);
              } else {
                reject(new Error('Upload failed'));
              }
            }
          });
        })
    );

    try {
      await Promise.all(uploadPromises);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error uploading files:', error);
      setShowLoadingIndicator(false);
      setIsLoading(false);
      setExpectedUploads(0);
      renderNotification({
        type: 'error',
        message: 'Fehler beim Hochladen der Dateien. Bitte versuchen Sie es erneut.'
      });
    }
  };

  const handleOnCancel = () => {
    if (attachments.length > 0) {
      setShowConfirmLeaveModal(true);
      return;
    }
    handleCloseModal();
  };

  const handleCloseModal = () => {
    setAttachments([]);
    setExpectedUploads(0);
    clearUploadsTempContainer();
    handleToggleModal();
    setUploadWasSuccessful(false);
    setIsLoading(false);
    setShowLoadingIndicator(false);
    setShowConfirmLeaveModal(false);
  };

  const fileContainer = fileContainers?.find((container) => container.id === fileContainerId);
  const modalTitle = title || fileContainer?.name || 'Dateien hochladen';
  const maxNumberOfFilesMessage = `Sie können ${
    maxNumberOfFiles === 1 ? `${maxNumberOfFiles} Datei` : `${maxNumberOfFiles} Dateien`
  } hochladen.`;
  const isSubmitButtonDisabled = isEmptyArray(attachments) || isLoading || uploadWasSuccessful;

  return (
    <>
      <Modal
        className="no-padding"
        open={isModalOpen}
        onCancel={handleOnCancel}
        onSubmit={handleOnSubmit}
        title={modalTitle}
        size="md"
        disableSubmit={isSubmitButtonDisabled}
      >
        {showLoadingIndicator && (
          <UploadDocumentsUpdateOverlay>
            <LoadingIndicator
              isLoading={isLoading}
              loadingMessage="Die Dokumente werden hinzugefügt."
              successMessage="Die Dokumente wurden erfolgreich hinzugefügt."
            />
          </UploadDocumentsUpdateOverlay>
        )}
        <ModalWrapper>
          <UploadDocumentsModalDescription>{description}</UploadDocumentsModalDescription>
          {!disableDropzone && (
            <UploadDocumentsUploadDropzoneWrapper {...getRootProps()}>
              <input {...getInputProps()} />
              <UploadDocumentsUploadDropzoneTextWrapper>
                <UploadDocumentsUploadDropzoneText>Datei auf diese Fläche ziehen</UploadDocumentsUploadDropzoneText>
                <UploadDocumentsUploadDropzoneText>
                  oder
                  <UploadDocumentsHighlightedText>Datei auswählen</UploadDocumentsHighlightedText>
                </UploadDocumentsUploadDropzoneText>
              </UploadDocumentsUploadDropzoneTextWrapper>
            </UploadDocumentsUploadDropzoneWrapper>
          )}
          <UploadDocumentsConstraints>
            {maxNumberOfFilesMessage} <br />
            {allowedFileTypes.description} <br /> Maximale Dateigröße: {maxFileSize.megaByte} MB
          </UploadDocumentsConstraints>
          {attachments.length > 0 && (
            <FileListWrapper>
              <UploadDocumentsModalFileListHeader>
                <UploadDocumentsModalFileListHeaderText>Hochgeladene Dateien</UploadDocumentsModalFileListHeaderText>
                <UploadDocumentsModalFileListCounter>
                  {attachments.length}/{maxNumberOfFiles}
                </UploadDocumentsModalFileListCounter>
              </UploadDocumentsModalFileListHeader>
              {renderFilesList()}
            </FileListWrapper>
          )}
        </ModalWrapper>
      </Modal>
      {showConfirmLeaveModal && (
        <ConfirmLeaveModal
          onCancelButtonClick={() => setShowConfirmLeaveModal(false)}
          onOkButtonClick={handleCloseModal}
          open={showConfirmLeaveModal}
        />
      )}
    </>
  );
};

UploadDocumentsModalComponent.displayName = 'UploadDocumentsModal';

const mapStateToProps = (state) => ({
  uploadsTempContainer: selectUploadsTempContainer(state),
  fileContainers: selectFileContainers(state)
});

const mapDispatchToProps = {
  uploadFile,
  clearUploadsTempContainer
};

export const UploadDocumentsModal = connect(mapStateToProps, mapDispatchToProps)(UploadDocumentsModalComponent);
